import {
  TextField,
  Avatar,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputAdornment,
  Typography,
  Box,
  Button
} from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { getObjectsWithFilter } from '../../api/apiClientCrud'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import styled from 'styled-components'

import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { ColorPicker } from 'mui-color'
import { BsImage } from 'react-icons/bs'
import JoditEditor from 'jodit-react'

import AvatarUser from '../misc/Avatar'
import Image from '../misc/Image'
import SelectField from './SelectField'
import MultiSelectField from './MultiSelectField'
import FreeSelectField from './FreeSelectField'
import ListSelectField from './ListSelectField'
import MyTableEditable from './table/MyTableEditable'
import { useFieldValidation } from '../hooks/useFieldValidation'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

const Field = forwardRef((props, ref) => {
  const {
    data,
    mode,
    field,
    onValueChange,
    onDateChange,
    handleChangeFile,
    isTable,
    editRow,
    onRowDataChange,
    isSaved
  } = props

  const { hasError, errorMessage, validateField } = useFieldValidation(field, data)

  useImperativeHandle(ref, () => ({ validateField, hasError, fieldTab: field.tab }), [
    validateField,
    hasError,
    field.tab
  ])

  const editorConfig = {
    readonly: false,
    height: 400,
    uploader: { insertImageAsBase64URI: true },
    toolbarAdaptive: false,
    toolbarSticky: false,
    toolbarButtonSize: 'middle',
    buttons: [
      'undo',
      'redo',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'eraser',
      'ul',
      'ol',
      'outdent',
      'indent',
      '|',
      'font',
      'fontsize',
      'brush',
      'paragraph',
      '|',
      'image',
      'link',
      'table',
      '|',
      'source',
      '|',
      {
        name: 'apply',
        tooltip: 'Appliquer',
        icon: 'save',
        exec: (editor) => {
          const event = {
            target: {
              type: 'joditEditor',
              name: editor.id,
              value: editor.value
            }
          }
          onValueChange(event)
        }
      }
    ]
  }

  const handleNumericKeyDown = (e) => {
    const allowed = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter']
    const isNumber = /^[0-9]$/.test(e.key)
    const isDecimal = e.key === '.' || e.key === ','

    if (!allowed.includes(e.key) && !isNumber && !isDecimal) e.preventDefault()
  }

  const handleNumericChange = (e) => {
    const val = e.target.value.replace(',', '.')
    onValueChange({ target: { name: field.name, value: val } })
  }

  useEffect(() => {
    const fetchDefaultValue = async () => {
      try {
        const res = await getObjectsWithFilter(field.defaultValue.object, field.defaultValue.filter(data))
        const defaultVal = res.data[field.defaultValue.object]?.[0]?.[field.defaultValue.value] ?? null
        onValueChange({ target: { name: field.name, value: defaultVal } })
      } catch {
        onValueChange({ target: { name: field.name, value: null } })
      }
    }

    if (
      ['text', 'text-multiline', 'currency'].includes(field.type) &&
      field.defaultValue &&
      data[field.defaultValue.trigger] !== undefined &&
      field.defaultValue.type === 'api'
    ) {
      fetchDefaultValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data[field.defaultValue?.trigger]])

  const renderTextField = (fieldType = 'text', overrides = {}) => {
    const isPassword = fieldType === 'password'

    return (
      <TextField
        sx={{ my: 0.5, ...overrides.sx }}
        fullWidth={field.fullWidth ?? false}
        name={field.name}
        label={!isTable ? field.label : null}
        variant={mode === 'view' || isTable ? 'standard' : 'outlined'}
        required={field.required || false}
        error={!!hasError}
        value={data[field.name] ?? ''}
        onChange={['float', 'currency', 'number'].includes(fieldType) ? handleNumericChange : onValueChange}
        slotProps={{
          input: {
            type: isPassword ? 'password' : ['float', 'currency'].includes(fieldType) ? 'number' : 'text',
            onKeyDown: ['float', 'currency', 'number'].includes(fieldType) ? handleNumericKeyDown : undefined,
            readOnly:
              mode === 'view' ||
              field.readOnly ||
              (field.enableEditing !== undefined && !field.enableEditing) ||
              (field.readOnlyWhen ? field.readOnlyWhen(editRow || data) : false),
            ...overrides.slotProps?.input
          },
          ...overrides.slotProps
        }}
      />
    )
  }

  const renderDateField = (type = 'date') => {
    const Picker = type === 'datetime' ? DateTimePicker : DatePicker
    const localFormat = 'YYYY-MM-DD HH:mm:ss'
    let value = null

    if (field.defaultValueFunction && field.defaultValueField && data[field.defaultValueField] !== undefined) {
      value = dayjs.utc(field.defaultValueFunction(data)).local()
    } else if (data[field.name]) {
      const dateString = data[field.name]
      const isUtcExplicit = /Z$|[+-]\d{2}:?\d{2}$/.test(dateString)
      value = isUtcExplicit ? dayjs.utc(dateString).local() : dayjs(dateString.replace('T', ' '), localFormat)
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='fr'>
        <Picker
          label={!isTable ? field.label : null}
          sx={{ my: 0.5 }}
          value={value}
          views={field.views}
          onChange={field.onDateChange || onDateChange(field.name, type)}
          readOnly={mode === 'view' || field.readOnly}
          slotProps={{
            textField: {
              fullWidth: field.fullWidth ?? false,
              error: !!hasError,
              variant: mode === 'view' || isTable ? 'standard' : 'outlined',
              required: field.required || false
            }
          }}
        />
      </LocalizationProvider>
    )
  }

  const buildField = (field) => {
    switch (field.type) {
      case 'password':
        return renderTextField('password')
      case 'select':
        return (
          <SelectField {...{ data, mode, field, onValueChange, errors: errorMessage, isError: hasError, isTable }} />
        )
      case 'freeselect':
        return (
          <FreeSelectField
            {...{
              field,
              isTable,
              onChangeF: field.onChangeF,
              optionsAPI: field.optionsAPI,
              dataId: field.dataId,
              editRow,
              onRowDataChange,
              isError: hasError,
              error: errorMessage
            }}
          />
        )
      case 'listSelect':
        return (
          <ListSelectField
            {...{
              field,
              isTable,
              onChangeF: field.onChangeF,
              optionsAPI: field.optionsAPI,
              dataId: field.dataId,
              editRow,
              onRowDataChange,
              isError: hasError,
              error: errorMessage
            }}
          />
        )
      case 'multiselect':
        return <MultiSelectField {...{ data, mode, field, onValueChange, error: errorMessage, isError: hasError }} />
      case 'color':
        return (
          <TextField
            variant='standard'
            label={isTable ? null : field.label}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <ColorPicker
                  hideTextfield
                  value={data[field.name]}
                  onChange={(e) =>
                    mode !== 'view' && onValueChange({ target: { name: field.name, value: '#' + e.hex } })
                  }
                />
              )
            }}
          />
        )
      case 'component':
        return field.component(data)
      case 'date':
        return renderDateField('date')
      case 'datetime':
        return renderDateField('datetime')
      case 'label':
        return <Typography sx={{ mt: 0.25, mb: 0.25, color: 'rgba(0,0,0,0.38)' }}>{field.label}</Typography>
      case 'joditEditor':
        return (
          <JoditEditor
            id={field.name}
            value={data[field.name]}
            config={{ ...editorConfig, readonly: mode === 'view' || field.readOnly }}
          />
        )
      case 'text':
        return renderTextField()
      case 'float':
        return renderTextField('float')
      case 'text-multiline':
        return (
          <TextField
            sx={{ my: 0.5 }}
            fullWidth={field.fullWidth ?? false}
            name={field.name}
            variant={mode === 'view' || isTable ? 'standard' : 'outlined'}
            label={isTable ? null : field.label}
            multiline
            rows={4}
            required={field.required || false}
            error={!!hasError}
            value={data[field.name] ?? ''}
            slotProps={{
              input: {
                readOnly:
                  mode === 'view' || field.readOnly || (field.enableEditing !== undefined && !field.enableEditing)
              }
            }}
            onChange={onValueChange}
            inputRef={field.ref}
          />
        )
      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                name={field.name}
                checked={data[field.name] || false}
                onChange={onValueChange}
                disabled={mode === 'view' || (field.readOnlyWhen ? !field.readOnlyWhen() : false)}
              />
            }
            label={isTable ? null : field.label}
          />
        )
      case 'file':
        return (
          mode !== 'view' && (
            <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
              <Button
                component='label'
                variant='contained'
                startIcon={<CloudUploadIcon />}
                onChange={handleChangeFile(field)}
                sx={{ mr: 0.5 }}
              >
                Choisissez un fichier
                <VisuallyHiddenInput type='file' />
              </Button>
              {data[field.name] && (
                <Typography sx={{ mt: 0.25, ml: 1, fontSize: 12 }}>{data[field.name].name}</Typography>
              )}
            </Box>
          )
        )
      case 'avatar':
        return field.file && field.file(data) ? (
          <TextField
            variant='standard'
            label={isTable ? null : field.label}
            InputProps={{
              disableUnderline: true,
              startAdornment: <AvatarUser size='logo' img={field.file(data)} />
            }}
          />
        ) : (
          <Avatar sx={{ height: 120, width: 120 }}>
            <AccountCircleIcon />
          </Avatar>
        )
      case 'image':
        return field.file && field.file(data) ? (
          <Box
            sx={{
              width: 120,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
          >
            <Image img={field.file(data)} />
          </Box>
        ) : (
          <Box
            sx={{
              height: 120,
              width: 160,
              border: '2px dashed #ccc',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#aaa',
              fontSize: '2rem'
            }}
          >
            <BsImage />
          </Box>
        )
      case 'currency':
        return (
          <TextField
            sx={{ my: 0.5 }}
            fullWidth={field.fullWidth ?? false}
            name={field.name}
            label={!isTable ? field.label : null}
            variant={mode === 'view' || isTable ? 'standard' : 'outlined'}
            required={field.required || false}
            error={!!hasError}
            type='number'
            inputProps={{ min: field.min, max: field.max, step: '0.01' }}
            InputProps={{
              startAdornment: <InputAdornment position='start'>{field.currency ?? '€'}</InputAdornment>
            }}
            slotProps={{
              input: {
                readOnly:
                  mode === 'view' || field.readOnly || (field.enableEditing !== undefined && !field.enableEditing)
              }
            }}
            value={data[field.name] ?? ''}
            onChange={onValueChange}
          />
        )
      case 'mytable':
        return (
          <MyTableEditable
            data={data}
            tableOptions={field.tableOptions}
            alldata={data}
            mode={mode}
            field={field}
            setData={field.tableOptions.setDataParent}
            error={errorMessage}
            isSaved={isSaved}
            columns={field.tableOptions.columns}
          />
        )
      default:
        return renderTextField()
    }
  }

  return (
    <>
      {buildField(field)}
      {hasError && <FormHelperText error>{errorMessage || ''}</FormHelperText>}
    </>
  )
})

export default Field
